import { useLanguage } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { Activity, BarChart3, Brain, GraduationCap, LineChart, Users } from "lucide-react";

const IMAGES = [
  "https://images.unsplash.com/photo-1586534738560-438efdf1d205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob3NwaXRhbCUyMG1hbmFnZW1lbnR8ZW58MHx8fHwxNzc2NjQzMzM5fDA&ixlib=rb-4.1.0&q=85",
  "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/ni62ugk2_Imagen%20pasillo%20hospital.png",
  "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/6qvg7wz8_IA%20Medsim.png",
  "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/jh2vfjtt_MEDSIM%20chica%20estudiando.png",
  "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/czctdd8x_Gemini_Generated_Image_fjv9lfjv9lfjv9lf.png",
  "https://customer-assets.emergentagent.com/job_medboard-exec/artifacts/6hb7x2ek_MEDSIM%20Dashboard.png",
];

const ICONS = [Activity, BarChart3, Brain, GraduationCap, LineChart, Users];

const GRID_CLASSES = [
  "lg:col-span-2 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-1 lg:row-span-1",
  "lg:col-span-2 lg:row-span-1",
];

const fadeUpInitial = { opacity: 0, y: 30 };
const fadeUpAnimate = { opacity: 1, y: 0 };
const headerViewport = { once: true, margin: "-100px" };
const cardViewport = { once: true, margin: "-50px" };
const headerTransition = { duration: 0.6 };

function FeatureCard({ card, index }) {
  const Icon = ICONS[index];
  return (
    <motion.div
      key={card.title}
      data-testid={`feature-card-${index}`}
      initial={fadeUpInitial}
      whileInView={fadeUpAnimate}
      viewport={cardViewport}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={`group relative bg-[#131b2f] border border-[#2a3449] rounded-2xl overflow-hidden hover:border-[#1a6cf5]/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#1a6cf5]/10 ${GRID_CLASSES[index]}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={IMAGES[index]}
          alt={card.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131b2f] via-[#131b2f]/50 to-transparent" />
        <div className="absolute bottom-4 left-6">
          <Icon className="text-[#1a6cf5]" size={28} />
        </div>
      </div>
      <div className="p-6 pt-2">
        <h3 className="font-playfair text-xl text-[#f8f9fa] mb-3">{card.title}</h3>
        <p className="font-outfit text-sm text-[#94a3b8] leading-relaxed">{card.desc}</p>
      </div>
    </motion.div>
  );
}

export default function Features() {
  const { t } = useLanguage();
  const cards = t("features.cards");

  return (
    <section id="features" data-testid="features-section" className="relative py-16 md:py-20 bg-[#0a0f1f]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={fadeUpInitial}
          whileInView={fadeUpAnimate}
          viewport={headerViewport}
          transition={headerTransition}
          className="text-center mb-16"
        >
          <h2
            data-testid="features-h2"
            className="font-playfair text-4xl md:text-5xl text-[#f8f9fa] tracking-tight mb-6"
          >
            {t("features.h2")}
          </h2>
          <p
            data-testid="features-intro"
            className="font-outfit text-base md:text-lg text-[#94a3b8] max-w-3xl mx-auto leading-relaxed"
          >
            {t("features.intro")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(cards) && cards.map((card, i) => (
            <FeatureCard key={card.title} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
